// ============================================================
// AttackToggles - Discrete toggle buttons for common attack modifiers.
//
// A RENDERER since v8.299.0: the record, the sum, the itemisation and
// the mutual-exclusion writer all live in engine/actions/toggleEntries,
// where the drum can reach them too. This file draws pills and reports
// which one was pressed.
// ============================================================
import styles from '../styles/AttackerPanel.module.css';
import clsx from '../utils/clsx';

import type { AttackToggleState } from '../engine/actions/toggleEntries';
import { applyStanceToggle } from '../engine/actions/toggleEntries';

interface ToggleDef {
  key: keyof AttackToggleState;
  label: string;
  value: number;
  meleeOnly?: boolean;
  /** Advisory action cost for the tooltip - displayed, never enforced. */
  cost?: string;
}

// No calledShot pill: the targeting console owns that toggle since
// v8.232.0 (a zone click engages penalty + location in one gesture).
// The advisory costs ride the tooltips - the drum used to carry them,
// and it holds only decisive actions now (v8.236.0).
const TOGGLE_DEFS: ToggleDef[] = [
  { key: 'aimHalf', label: 'Aim', value: 10, cost: 'Half Action' },
  { key: 'aimFull', label: 'Full Aim', value: 20, cost: 'Full Action' },
  { key: 'charge', label: 'Charge', value: 10, meleeOnly: true, cost: 'Full Action, 4m+ run-up' },
  { key: 'gangUp', label: 'Gang Up', value: 10, meleeOnly: true },
  // +20 per the rulebook - the sim granted +30 until v8.212.0 (ADR 2026-08-28).
  { key: 'allOutAttack', label: 'All-Out', value: 20, meleeOnly: true, cost: 'Full Action, no Dodge/Parry' },
  { key: 'guardedAttack', label: 'Guarded', value: -10, meleeOnly: true, cost: 'Full Action, +10 Dodge/Parry' },
];

interface AttackTogglesProps {
  toggles: AttackToggleState;
  setToggles: (t: AttackToggleState) => void;
  isMelee: boolean;
  disabled?: boolean;
  hasBerserkCharge?: boolean;
  hasHammerBlow?: boolean;
  /**
   * Defensive Stance rides the stance row since v8.236.0 (the drum
   * holds only decisive actions). It is a CONDITION on the actor, not
   * an attack toggle - the caller owns the state and the write.
   */
  defensiveStance?: boolean;
  onToggleDefensiveStance?: (() => void) | null;
}

export default function AttackToggles({
  toggles,
  setToggles,
  isMelee,
  disabled,
  hasBerserkCharge = false,
  hasHammerBlow = false,
  defensiveStance = false,
  onToggleDefensiveStance = null,
}: AttackTogglesProps) {
  const talents = { hasBerserkCharge, hasHammerBlow };
  return (
    <div className={styles.quickModRow}>
      {TOGGLE_DEFS.filter((t) => !t.meleeOnly || isMelee).map((def) => (
        <TogglePill
          key={def.key}
          def={def}
          active={toggles[def.key]}
          talents={talents}
          disabled={disabled}
          onToggle={() => setToggles(applyStanceToggle(toggles, def.key))}
        />
      ))}
      {onToggleDefensiveStance && (
        <button
          className={clsx(defensiveStance ? styles.stanceOn : styles.stanceBtn)}
          onClick={onToggleDefensiveStance}
          disabled={disabled}
          aria-pressed={defensiveStance}
          title="Defensive Stance (Full Action): no attacks until your next turn; foes take -20 WS against you; one extra Reaction."
        >
          Defensive Stance
        </button>
      )}
    </div>
  );
}

interface Talents {
  /** Berserk Charge makes a charge +20. */
  hasBerserkCharge: boolean;
  /** Hammer Blow adds damage and penetration to an All-Out Attack. */
  hasHammerBlow: boolean;
}

/** A pill's shown value, and the Hammer Blow note when it applies. */
function pillValue(def: ToggleDef, active: boolean, talents: Talents) {
  const value = def.key === 'charge' && talents.hasBerserkCharge ? 20 : def.value;
  const hammerBlow = def.key === 'allOutAttack' && talents.hasHammerBlow && active;
  return { value, sign: value > 0 ? '+' : '', note: hammerBlow ? ' +HB' : '' };
}

interface TogglePillProps {
  def: ToggleDef;
  active: boolean;
  talents: Talents;
  disabled?: boolean;
  onToggle: () => void;
}

function TogglePill({ def, active, talents, disabled, onToggle }: TogglePillProps) {
  const { value, sign, note } = pillValue(def, active, talents);
  const title =
    `${def.label} (${sign}${value})` + (def.cost ? ' - ' + def.cost : '') + (note ? ' - Hammer Blow: +2 dmg/pen' : '');
  return (
    <button
      className={clsx(active ? styles.stanceOn : styles.stanceBtn)}
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={active}
      title={title}
    >
      {def.label} {sign}
      {value}
      {note}
    </button>
  );
}
